// src/components/exports/PropertyDocumentsUploader.tsx
import { useCallback, useRef, useState } from "react";
import { Upload, FileText, X, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { Progress } from "@/shared/ui/progress";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/shared/integrations/supabase/client";
import { uploadPropertyDocument } from "@/shared/lib/firebaseStorage";
import { firebaseAuth } from "@/shared/integrations/firebase/client";
import { useToast } from "@/shared/hooks/use-toast";
import { cn } from "@/shared/lib/utils";

interface PropertyDocumentsUploaderProps {
  propertyId: string;
}

const MAX_SIZE_BYTES = 100 * 1024 * 1024; // 100 MB
const MAX_PARALLEL = 3;
const SOFT_FILE_CAP = 50;

type ItemStatus =
  | { kind: "pending" }
  | { kind: "uploading"; percent: number }
  | { kind: "success" }
  | { kind: "failed"; message: string };

interface QueueItem {
  id: string;
  file: File;
  status: ItemStatus;
}

export function PropertyDocumentsUploader({ propertyId }: PropertyDocumentsUploaderProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [items, setItems] = useState<QueueItem[]>([]);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadOne = useCallback(async (item: QueueItem) => {
    const updateStatus = (status: ItemStatus) =>
      setItems((prev) => prev.map((it) => (it.id === item.id ? { ...it, status } : it)));

    try {
      updateStatus({ kind: "uploading", percent: 0 });

      const { path, downloadUrl } = await uploadPropertyDocument(propertyId, item.file, {
        onProgress: (frac) => updateStatus({ kind: "uploading", percent: Math.round(frac * 100) }),
      });

      const uploadedBy = firebaseAuth.currentUser?.uid ?? '';

      const { error: insertErr } = await supabase.from("documents").insert({
        property_id: propertyId,
        document_name: item.file.name,
        file_path: downloadUrl,
        file_size: item.file.size,
        mime_type: item.file.type || "application/octet-stream",
        uploaded_by: uploadedBy,
        document_type: "Document",
      });

      if (insertErr) {
        updateStatus({ kind: "failed", message: `Saved to storage but DB insert failed: ${insertErr.message}` });
        toast({
          title: "Database insert failed",
          description: `${item.file.name}: ${insertErr.message}. The file is in Firebase Storage at ${path} but isn't tracked in the documents table.`,
          variant: "destructive",
        });
        return;
      }

      updateStatus({ kind: "success" });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      updateStatus({ kind: "failed", message });
    }
  }, [propertyId, toast]);

  const processQueue = useCallback(async (queue: QueueItem[]) => {
    let cursor = 0;
    const workers = Array.from({ length: Math.min(MAX_PARALLEL, queue.length) }, async () => {
      while (cursor < queue.length) {
        const myIndex = cursor++;
        const item = queue[myIndex];
        await uploadOne(item);
      }
    });
    await Promise.all(workers);

    queryClient.invalidateQueries({ queryKey: ["property-all-files", propertyId] });
  }, [propertyId, queryClient, uploadOne]);

  const addFiles = useCallback(async (files: File[]) => {
    if (files.length === 0) return;

    if (files.length > SOFT_FILE_CAP) {
      toast({
        title: `Selected ${files.length} files`,
        description: `Uploading all of them. Consider splitting into smaller batches if this is unusual.`,
      });
    }

    const accepted: QueueItem[] = [];
    const rejected: { file: File; reason: string }[] = [];
    for (const file of files) {
      if (file.size === 0) {
        rejected.push({ file, reason: "File is empty" });
        continue;
      }
      if (file.size > MAX_SIZE_BYTES) {
        rejected.push({ file, reason: `Exceeds 100 MB limit (file is ${(file.size / 1024 / 1024).toFixed(1)} MB)` });
        continue;
      }
      accepted.push({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${file.name}`,
        file,
        status: { kind: "pending" },
      });
    }

    if (rejected.length > 0) {
      setItems((prev) => [
        ...prev,
        ...rejected.map((r) => ({
          id: `rej-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          file: r.file,
          status: { kind: "failed" as const, message: r.reason },
        })),
      ]);
    }

    if (accepted.length === 0) return;

    setItems((prev) => [...prev, ...accepted]);
    await processQueue(accepted);
  }, [toast, processQueue]);

  const retryOne = useCallback((id: string) => {
    const item = items.find((it) => it.id === id);
    if (!item) return;
    void uploadOne({ ...item, status: { kind: "pending" } });
  }, [items, uploadOne]);

  const removeOne = useCallback((id: string) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
  }, []);

  const onDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    const files = Array.from(e.dataTransfer.files);
    void addFiles(files);
  }, [addFiles]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Upload className="h-4 w-4" />
          Upload Documents
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => fileInputRef.current?.click()}
          className={cn(
            "border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors",
            dragging
              ? "border-primary bg-primary/5"
              : "border-border/60 hover:border-border bg-muted/20"
          )}
        >
          <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm font-medium text-foreground">Drop files here or click to browse</p>
          <p className="text-xs text-muted-foreground mt-1">
            Up to 100 MB per file. Any file type.
          </p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              void addFiles(files);
              e.target.value = "";
            }}
          />
        </div>

        {items.length > 0 && (
          <ul className="space-y-2">
            {items.map((item) => (
              <li
                key={item.id}
                className="flex items-center gap-2 rounded-md border border-border/40 bg-muted/10 px-3 py-2"
              >
                <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-foreground truncate">{item.file.name}</p>
                  {item.status.kind === "uploading" && (
                    <Progress value={item.status.percent} className="h-1 mt-1" />
                  )}
                  {item.status.kind === "failed" && (
                    <p className="text-[10px] text-destructive mt-0.5">{item.status.message}</p>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {item.status.kind === "uploading" && (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  )}
                  {item.status.kind === "success" && (
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                  )}
                  {item.status.kind === "failed" && (
                    <>
                      <AlertCircle className="h-4 w-4 text-destructive" />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => retryOne(item.id)}
                        className="h-6 px-2 text-xs"
                      >
                        Retry
                      </Button>
                    </>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeOne(item.id)}
                    className="h-6 w-6"
                    aria-label="Remove from list"
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
